import { createContext, useContext, useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { resolveAccess } from '../lib/access';
import type { AccessProfile } from '../types';

interface AuthState {
  session: Session | null;
  profile: AccessProfile | null;
  loading: boolean;
  error: string | null;
  signIn(email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
  refresh(): Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<AccessProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load(nextSession: Session | null) {
    setSession(nextSession);
    setError(null);
    if (!nextSession) {
      setProfile(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setProfile(await resolveAccess(nextSession.user));
    } catch (reason) {
      setProfile(null);
      setError(reason instanceof Error ? reason.message : 'Access could not be resolved.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => load(data.session));
    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => { void load(nextSession); });
    return () => data.subscription.unsubscribe();
  }, []);

  const value = useMemo<AuthState>(() => ({
    session,
    profile,
    loading,
    error,
    async signIn(email, password) {
      setLoading(true);
      const { data, error: signInError } = await supabase.auth.signInWithPassword({ email, password });
      if (signInError) {
        setLoading(false);
        throw signInError;
      }
      await load(data.session);
    },
    async signOut() {
      await supabase.auth.signOut();
      await load(null);
    },
    async refresh() {
      if (session) await load(session);
    },
  }), [session, profile, loading, error]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
